import { useState } from "react";
import { X, Trash2, Minus, Plus, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { createCheckoutSession } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export const CartDrawer = () => {
  const {
    items, open, setOpen, updateQty, removeItem,
    subtotal, tax, total, clear,
  } = useCart();

  const [step, setStep] = useState("cart"); // cart | details
  const [fulfillment, setFulfillment] = useState("pickup");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const close = () => {
    setOpen(false);
    setTimeout(() => setStep("cart"), 200);
  };

  const startCheckout = async () => {
    if (items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }
    if (!name.trim() || !phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    if (fulfillment === "delivery" && !address.trim()) {
      toast.error("Delivery address is required");
      return;
    }
    setSubmitting(true);
    try {
      const lines = items.map((it) => ({ item_id: it.id, quantity: it.quantity }));
      const noteParts = [];
      items.forEach((it) => { if (it.sauce) noteParts.push(`${it.name}: ${it.sauce}`); });
      const finalNotes = [notes.trim(), noteParts.join(" | ")].filter(Boolean).join(" — ");

      const data = await createCheckoutSession({
        lines,
        origin_url: window.location.origin,
        fulfillment,
        customer_name: name,
        customer_phone: phone,
        customer_email: email || null,
        delivery_address: fulfillment === "delivery" ? address : null,
        notes: finalNotes || null,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout");
        setSubmitting(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout failed";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        data-testid="cart-overlay"
        className={`fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={close}
      />
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[70] h-full w-full sm:w-[480px] bg-[#0A0A0A] border-l-2 border-[#E60000] flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-[#333]">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#E60000] font-bold">Your Order</div>
            <h2 className="font-heading text-3xl text-white">
              {step === "cart" ? "Cart" : "Checkout"}
            </h2>
          </div>
          <button data-testid="close-cart-btn" onClick={close} className="text-white hover:text-[#E60000] p-2">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ? (
            <div data-testid="cart-empty" className="text-center py-20 text-white/50 uppercase tracking-widest text-sm">
              Cart is empty. Go add some heat.
            </div>
          ) : step === "cart" ? (
            <ul className="space-y-4">
              {items.map((it) => (
                <li
                  key={it.key}
                  data-testid={`cart-line-${it.id}`}
                  className="flex gap-3 border border-[#222] p-3 bg-[#141414]"
                >
                  {it.image_url && (
                    <img src={it.image_url} alt={it.name} className="w-20 h-20 object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h3 className="font-heading text-lg text-white leading-tight">{it.name}</h3>
                      <button
                        data-testid={`remove-line-${it.id}`}
                        onClick={() => removeItem(it.key)}
                        className="text-white/50 hover:text-[#E60000]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {it.sauce && (
                      <div className="text-xs text-[#E60000] uppercase tracking-widest mt-1">Sauce: {it.sauce}</div>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <div className="inline-flex items-center border border-[#333]">
                        <button
                          data-testid={`qty-decrement-${it.id}`}
                          onClick={() => updateQty(it.key, it.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center text-white hover:bg-[#E60000]"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm text-white">{it.quantity}</span>
                        <button
                          data-testid={`qty-increment-${it.id}`}
                          onClick={() => updateQty(it.key, it.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center text-white hover:bg-[#E60000]"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-white font-bold">${(it.price * it.quantity).toFixed(2)}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/70 mb-2 block">Fulfillment</Label>
                <RadioGroup value={fulfillment} onValueChange={setFulfillment} className="grid grid-cols-2 gap-3">
                  {["pickup", "delivery"].map((opt) => (
                    <label
                      key={opt}
                      data-testid={`fulfillment-${opt}`}
                      className={`flex items-center gap-2 p-3 border-2 cursor-pointer uppercase tracking-widest text-sm font-bold ${
                        fulfillment === opt ? "border-[#E60000] bg-[#E60000]/10 text-white" : "border-[#333] text-white/70"
                      }`}
                    >
                      <RadioGroupItem value={opt} className="border-white" />
                      {opt}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <Field label="Full Name *" testId="checkout-name">
                <Input
                  data-testid="checkout-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-[#141414] border-2 border-[#333] rounded-none h-11 text-white"
                  placeholder="Your name"
                />
              </Field>
              <Field label="Phone *" testId="checkout-phone">
                <Input
                  data-testid="checkout-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-[#141414] border-2 border-[#333] rounded-none h-11 text-white"
                  placeholder="(901) 555-0100"
                />
              </Field>
              <Field label="Email (optional)" testId="checkout-email">
                <Input
                  data-testid="checkout-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#141414] border-2 border-[#333] rounded-none h-11 text-white"
                  placeholder="you@example.com"
                  type="email"
                />
              </Field>
              {fulfillment === "delivery" && (
                <Field label="Delivery Address *" testId="checkout-address">
                  <Textarea
                    data-testid="checkout-address-input"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    rows={2}
                    className="bg-[#141414] border-2 border-[#333] rounded-none text-white"
                    placeholder="Street, City, ZIP"
                  />
                </Field>
              )}
              <Field label="Order Notes (optional)" testId="checkout-notes">
                <Textarea
                  data-testid="checkout-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="bg-[#141414] border-2 border-[#333] rounded-none text-white"
                  placeholder="Extra ranch, no onions, etc."
                />
              </Field>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t-2 border-[#333] p-5 space-y-3 bg-[#0A0A0A]">
            <Row label="Subtotal" value={subtotal} testId="cart-subtotal" />
            <Row label="Tax" value={tax} testId="cart-tax" />
            <div className="flex items-end justify-between border-t border-[#333] pt-3">
              <div className="text-xs uppercase tracking-widest text-white/70">Total</div>
              <div data-testid="cart-total" className="font-heading text-3xl text-white">
                ${total.toFixed(2)}
              </div>
            </div>
            {step === "cart" ? (
              <button
                data-testid="proceed-to-checkout-btn"
                onClick={() => setStep("details")}
                className="w-full h-12 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-widest text-sm border-2 border-transparent hover:border-white transition-colors"
              >
                Checkout
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="back-to-cart-btn"
                  onClick={() => setStep("cart")}
                  className="h-12 bg-transparent text-white border-2 border-[#333] hover:border-[#E60000] font-bold uppercase tracking-widest text-sm transition-colors"
                  disabled={submitting}
                >
                  Back
                </button>
                <button
                  data-testid="pay-now-btn"
                  onClick={startCheckout}
                  disabled={submitting}
                  className="h-12 bg-[#E60000] hover:bg-[#FF3333] text-white font-bold uppercase tracking-widest text-sm border-2 border-transparent hover:border-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {submitting ? "Redirecting..." : "Pay with Stripe"}
                </button>
              </div>
            )}
            {step === "cart" && (
              <button
                data-testid="clear-cart-btn"
                onClick={() => clear()}
                className="w-full text-xs uppercase tracking-widest text-white/50 hover:text-[#E60000] py-2"
              >
                Clear cart
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between text-sm">
    <span className="text-white/60 uppercase tracking-widest">{label}</span>
    <span data-testid={testId} className="text-white font-semibold">${value.toFixed(2)}</span>
  </div>
);

const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <Label className="text-xs uppercase tracking-widest text-white/70 mb-2 block">{label}</Label>
    {children}
  </div>
);
